import { COLOURS } from '../../utils/thermal/roi';

/*
 * A line chart over time (docs/FIELD_AI_VISION_SPEC.md section 27, built
 * here for the Thermal & Metabolic tab, phase C3): one line per series, each
 * point a button, an optional range (lo-hi) drawn as a whisker through it.
 * Hand-rolled SVG, as MonthlyBarChart: colours through style (theme tokens),
 * the series in the Okabe-Ito palette, which every common colour-vision
 * deficiency tells apart, and each named in a legend below -- never by
 * colour alone.
 *
 *   series      [{key, name, points: [{t, value, lo?, hi?, ...}]}]  t in ms
 *   yLabel      the axis's title ("Surface temperature (°C)")
 *   formatValue how a value is written in a point's title
 *   formatTime  how a time is written on the axis and in a title
 *   onPoint     (point, series) when a point is chosen (click, Enter, Space)
 *   pointLabel  (point, series) -> the point's accessible name
 */
const WIDTH = 640;
const HEIGHT = 260;
const PAD = { top: 12, right: 16, bottom: 34, left: 52 };

/** About `count` round steps covering lo..hi. */
export function ticks(lo, hi, count = 5) {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];
  if (lo === hi) return [lo];
  const raw = (hi - lo) / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((s) => s >= raw) || raw;
  const out = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) out.push(Number(v.toFixed(10)));
  return out;
}

export default function TrendLine({
  series, yLabel, formatValue = (v) => String(v), formatTime = (t) => new Date(t).toISOString().slice(0, 10),
  onPoint, pointLabel, emptyMessage = '', testId = 'trend-line',
}) {
  const all = (series || []).flatMap((s) => s.points);
  if (!all.length) {
    return <p style={{ color: 'var(--c-text-faint-fg)', fontSize: 13, margin: '8px 0' }} data-testid={`${testId}-empty`}>{emptyMessage}</p>;
  }
  const values = all.flatMap((p) => [p.value, p.lo ?? p.value, p.hi ?? p.value]).filter(Number.isFinite);
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  if (lo === hi) { lo -= 1; hi += 1; }
  const margin = (hi - lo) * 0.08;
  lo -= margin;
  hi += margin;
  let t0 = Math.min(...all.map((p) => p.t));
  let t1 = Math.max(...all.map((p) => p.t));
  if (t0 === t1) { t0 -= 12 * 3600e3; t1 += 12 * 3600e3; }
  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const x = (t) => PAD.left + ((t - t0) / (t1 - t0)) * innerW;
  const y = (v) => PAD.top + (1 - (v - lo) / (hi - lo)) * innerH;
  const yTicks = ticks(lo, hi, 5).filter((v) => v >= lo && v <= hi);
  const xTicks = [...new Set([t0, (t0 + t1) / 2, t1].map((t) => formatTime(t)))]
    .map((label, i, labels) => ({ label, t: labels.length === 1 ? (t0 + t1) / 2 : t0 + (i / (labels.length - 1)) * (t1 - t0) }));
  const colour = (i) => COLOURS[i % COLOURS.length];
  const choose = (event, point, s) => {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    if (event.type === 'keydown') event.preventDefault();
    onPoint?.(point, s);
  };

  return (
    <figure style={{ margin: 0 }} data-testid={testId}>
      <svg width="100%" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="group" aria-label={yLabel}
        style={{ display: 'block', maxWidth: WIDTH }}>
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(v)} y2={y(v)} strokeWidth={1}
              style={{ stroke: 'var(--c-border)' }} />
            <text x={PAD.left - 6} y={y(v) + 3} textAnchor="end" fontSize="10" style={{ fill: 'var(--c-text-3)' }}>
              {formatValue(v)}</text>
          </g>
        ))}
        <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={PAD.top + innerH} strokeWidth={1}
          style={{ stroke: 'var(--c-border-strong-4)' }} />
        <line x1={PAD.left} x2={WIDTH - PAD.right} y1={PAD.top + innerH} y2={PAD.top + innerH} strokeWidth={1}
          style={{ stroke: 'var(--c-border-strong-4)' }} />
        {xTicks.map(({ label, t }) => (
          <text key={`x${label}`} x={x(t)} y={HEIGHT - PAD.bottom + 16} textAnchor="middle" fontSize="10"
            style={{ fill: 'var(--c-text-faint-2)' }}>{label}</text>
        ))}
        <text x={12} y={PAD.top + innerH / 2} fontSize="10" textAnchor="middle"
          transform={`rotate(-90 12 ${PAD.top + innerH / 2})`} style={{ fill: 'var(--c-text-3)' }}>{yLabel}</text>
        {series.map((s, i) => (
          <g key={s.key} data-testid={`${testId}-series-${s.key}`}>
            {s.points.length > 1 && (
              <polyline fill="none" strokeWidth={2} points={s.points.map((p) => `${x(p.t)},${y(p.value)}`).join(' ')}
                style={{ stroke: colour(i) }} />
            )}
            {s.points.map((p, j) => (
              <g key={j}>
                {Number.isFinite(p.lo) && Number.isFinite(p.hi) && p.hi > p.lo && (
                  <line x1={x(p.t)} x2={x(p.t)} y1={y(p.lo)} y2={y(p.hi)} strokeWidth={2} style={{ stroke: colour(i) }} />
                )}
                <circle cx={x(p.t)} cy={y(p.value)} r={5} strokeWidth={2}
                  role={onPoint ? 'button' : 'img'} tabIndex={onPoint ? 0 : undefined}
                  aria-label={pointLabel ? pointLabel(p, s) : `${s.name}: ${formatValue(p.value)}`}
                  data-testid={`${testId}-point-${s.key}-${j}`}
                  onClick={onPoint ? (e) => choose(e, p, s) : undefined}
                  onKeyDown={onPoint ? (e) => choose(e, p, s) : undefined}
                  style={{ fill: 'var(--c-surface)', stroke: colour(i), cursor: onPoint ? 'pointer' : 'default' }}>
                  <title>{pointLabel ? pointLabel(p, s) : `${s.name}: ${formatValue(p.value)}`}</title>
                </circle>
              </g>
            ))}
          </g>
        ))}
      </svg>
      <ul style={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 12, padding: 0, margin: '6px 0 0', fontSize: 12 }}
        data-testid={`${testId}-legend`}>
        {series.map((s, i) => (
          <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span aria-hidden="true" style={{ width: 14, height: 3, background: colour(i), display: 'inline-block' }} />
            {s.name}
          </li>
        ))}
      </ul>
    </figure>
  );
}
